import React from 'react';
import { ItineraryTypes } from '../types';
import { ItineraryOverviewGroup } from './ItineraryOverviewGroup';
import { ItineraryOverviewItem } from './ItineraryOverviewItem';
import { differenceInDays } from 'date-fns';
import { TFilterOption } from './ItineraryOverviewFilter';

export interface IItineraryOverviewProps {
  datedItems: ItineraryTypes['ItineraryItem'][];
  undatedItems: ItineraryTypes['ItineraryItem'][];
  earliestDate?: string;
  filter?: TFilterOption;
}

interface IDestinationGroup {
  destinationMarker: ItineraryTypes['ItineraryItem'];
  items: ItineraryTypes['ItineraryItem'][];
  startDay: number;
  endDay: number;
}

export const ItineraryOverview = ({
  datedItems,
  undatedItems,
  earliestDate,
  filter = 'FULL_OVERVIEW',
}: IItineraryOverviewProps) => {
  const getDayNumber = (date: string | undefined): number => {
    if (!date || !earliestDate) return 1;
    const diff = differenceInDays(new Date(date), new Date(earliestDate));
    return diff + 1;
  };

  // Two-pass approach to handle items that appear before destination markers
  
  // Pass 1: Find all destination markers and create groups
  const destinationGroups: IDestinationGroup[] = [];
  const destinationMarkers = datedItems.filter(item => item.itemType === 'destinationMarker');
  
  destinationMarkers.forEach(marker => {
    destinationGroups.push({
      destinationMarker: marker,
      items: [marker],
      startDay: getDayNumber(marker.date),
      endDay: getDayNumber(marker.date),
    });
  });

  // Pass 2: Assign all other items to the appropriate destination group
  const nonDestinationItems = datedItems.filter(item => item.itemType !== 'destinationMarker');
  
  nonDestinationItems.forEach(item => {
    // Find the best matching destination group for this item
    let bestGroup: IDestinationGroup | null = null;
    
    // Look for a destination group that has the same date or encompasses this item's date
    for (const group of destinationGroups) {
      const groupDate = group.destinationMarker.date;
      const itemDate = item.date;
      
      if (groupDate && itemDate) {
        // If dates match exactly, this is the perfect group
        if (groupDate === itemDate) {
          bestGroup = group;
          break;
        }
        
        // If no exact match found yet, use the chronologically closest destination
        if (!bestGroup || (itemDate >= groupDate && (!bestGroup.destinationMarker.date || groupDate > bestGroup.destinationMarker.date))) {
          bestGroup = group;
        }
      }
    }
    
    // If we found a suitable group, add the item to it
    if (bestGroup) {
      bestGroup.items.push(item);
      
      // Update the group's end day based on this item
      const itemStartDay = getDayNumber(item.date);
      const itemEndDay = item.endDate ? getDayNumber(item.endDate) : itemStartDay;
      const latestDayForItem = Math.max(itemStartDay, itemEndDay);

      if (latestDayForItem > bestGroup.endDay) {
        bestGroup.endDay = latestDayForItem;
      }
    }
  });

  return (
    <div className="w-full m-0 mx-auto mb-40px">
      <>
        {/* Destination Groups */}
        <div className="bg-gray-3 px-5 pb-5">
          {destinationGroups.map((group, index) => (
            <ItineraryOverviewGroup
              key={group.destinationMarker.uuid}
              destinationMarker={group.destinationMarker}
              items={group.items}
              startDay={group.startDay}
              endDay={group.endDay}
              isOpenByDefault={false}
              filter={filter}
            />
          ))}

          {/* Unscheduled Products */}
          {(() => {
            // Filter unscheduled items based on current filter
            const filteredUndatedItems = undatedItems.filter(item => {
              if (filter === 'FULL_OVERVIEW') return true;
              if (filter === 'STAYS') return item.itemType === 'accommodation' || item.itemType === 'hotelMarker';
              if (filter === 'TRANSFERS') return item.itemType === 'transfer';
              if (filter === 'GROUND_SERVICES') return item.itemType === 'groundService';
              if (filter === 'EXPERIENCES') return item.itemType === 'bespoke';
              return false;
            });

            // Only show the unscheduled group if there are items after filtering
            if (filteredUndatedItems.length === 0) {
              return null;
            }

            return (
              <ItineraryOverviewGroup
                key="unscheduled-products"
                items={undatedItems}
                isOpenByDefault={false}
                filter={filter}
                customTitle="UNSCHEDULED PRODUCTS"
              />
            );
          })()}
        </div>
      </>
    </div>
  );
};
